import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Play, 
  Trash2, 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  Clock,
  Target,
  AlertTriangle,
  Loader2,
  ChevronRight,
  X
} from 'lucide-react'
import { backtestsApi, strategiesApi } from '../lib/api'
import { formatCurrency, formatPercent, formatDate } from '../lib/utils'
import { cn } from '../lib/utils'
import { BacktestDetailModal } from '../components/BacktestDetailModal'

interface Backtest {
  id: string
  strategyId: string
  strategy: { name: string }
  startDate: string
  endDate: string
  initialCapital: number
  finalCapital: number
  totalReturn: number
  totalTrades: number
  winningTrades: number
  losingTrades: number
  winRate: number
  sharpeRatio: number | null
  maxDrawdown: number
  profitFactor: number | null
  avgTradeReturn?: number
  trades?: any[]
  equityCurve?: any[]
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  createdAt: string
  completedAt: string | null
}

export default function Backtests() {
  const queryClient = useQueryClient()
  const [showNewModal, setShowNewModal] = useState(false)
  const [selectedBacktest, setSelectedBacktest] = useState<Backtest | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['backtests'],
    queryFn: () => backtestsApi.getAll().then(r => r.data),
  })

  const { data: stats } = useQuery({
    queryKey: ['backtestStats'],
    queryFn: () => backtestsApi.getStats().then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => backtestsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtests'] })
      queryClient.invalidateQueries({ queryKey: ['backtestStats'] })
    },
  })

  const backtests: Backtest[] = data?.backtests || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backtesting Engine</h1>
          <p className="text-gray-600">
            Test your strategies on historical data
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Play className="w-4 h-4 mr-2" />
          Run Backtest
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Backtests</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalBacktests || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">{stats?.completedBacktests || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Running</p>
          <p className="text-2xl font-bold text-blue-600">{stats?.runningBacktests || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Avg Return</p>
          <p className={cn(
            'text-2xl font-bold',
            (stats?.averageReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {formatPercent((stats?.averageReturn || 0) / 100)}
          </p>
        </div>
      </div>

      {/* Backtests List */}
      {backtests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No backtests yet</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Run your first backtest to see how your strategy would have performed 
            on historical Polymarket data.
          </p>
          <button
            onClick={() => setShowNewModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Run Your First Backtest
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Strategy</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Return</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Trades</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Win Rate</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Max DD</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {backtests.map((backtest) => (
                <tr 
                  key={backtest.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedBacktest(backtest)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{backtest.strategy.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      {formatDate(backtest.startDate)} - {formatDate(backtest.endDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={cn(
                      'font-medium',
                      backtest.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {backtest.totalReturn >= 0 ? '+' : ''}{formatPercent(backtest.totalReturn / 100)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-600">
                    {backtest.totalTrades}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={cn(
                      backtest.winRate >= 50 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {backtest.winRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-red-600">
                    {formatPercent(backtest.maxDrawdown / 100)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={cn(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      backtest.status === 'COMPLETED' && 'bg-green-100 text-green-700',
                      backtest.status === 'RUNNING' && 'bg-blue-100 text-blue-700',
                      backtest.status === 'FAILED' && 'bg-red-100 text-red-700',
                      backtest.status === 'CANCELLED' && 'bg-gray-100 text-gray-700'
                    )}>
                      {backtest.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm('Delete this backtest?')) {
                          deleteMutation.mutate(backtest.id)
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Backtest Modal */}
      {showNewModal && (
        <NewBacktestModal onClose={() => setShowNewModal(false)} />
      )}

      {/* Detail Modal */}
      {selectedBacktest && (
        <BacktestDetailModal
          backtest={selectedBacktest}
          onClose={() => setSelectedBacktest(null)}
        />
      )}
    </div>
  )
}

// New Backtest Modal
function NewBacktestModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [strategyId, setStrategyId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [initialCapital, setInitialCapital] = useState('10000')

  const { data: strategies } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesApi.getAll().then(r => r.data),
  })

  const runMutation = useMutation({
    mutationFn: () => backtestsApi.run({
      strategyId,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      initialCapital: parseFloat(initialCapital),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtests'] })
      queryClient.invalidateQueries({ queryKey: ['backtestStats'] })
      onClose()
    },
  })

  const canSubmit = strategyId && startDate && endDate && initialCapital

  // Set default dates
  const today = new Date()
  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Run Backtest</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault()
            runMutation.mutate()
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Strategy</label>
            <select
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select a strategy...</option>
              {strategies?.strategies?.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || today.toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today.toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Initial Capital ($)</label>
            <input
              type="number"
              value={initialCapital}
              onChange={(e) => setInitialCapital(e.target.value)}
              min="100"
              step="100"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              required
            />
          </div>

          {runMutation.error && (
            <div className="text-sm text-red-600">
              Failed to start backtest. Please try again.
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || runMutation.isPending}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {runMutation.isPending ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Starting...
                </span>
              ) : (
                'Run Backtest'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
